import { ANALYTICS_EVENT_NAMES } from "analytics/catalog/events";
import { buildCommonEventProps } from "analytics/core/commonProps";
import { trackEvent } from "analytics/core/tracker";
import type { SubscriptionCancellationTrackingProps } from "interfaces/subscription-cancellation";

export type BillingAnalyticsContext = {
  pathname: string;
  search: string;
  businessId?: string | null;
  userId?: string | null;
  userEmail?: string | null;
  planType?: string | null;
  currency?: string | null;
};

const buildUpgradeModalProps = (
  context: BillingAnalyticsContext,
): Record<string, unknown> => {
  return buildCommonEventProps({
    screenKey: "upgrade_modal",
    pathname: context.pathname,
    search: context.search,
    businessId: context.businessId ?? null,
    userId: context.userId ?? null,
    userEmail: context.userEmail ?? null,
    planType: context.planType ?? null,
    currency: context.currency ?? null,
  });
};

const buildCheckoutProps = (
  context: BillingAnalyticsContext,
): Record<string, unknown> => {
  return buildCommonEventProps({
    screenKey: "checkout",
    pathname: context.pathname,
    search: context.search,
    businessId: context.businessId ?? null,
    userId: context.userId ?? null,
    userEmail: context.userEmail ?? null,
    planType: context.planType ?? null,
    currency: context.currency ?? null,
  });
};

const buildSubscriptionCancellationProps = (
  context: BillingAnalyticsContext,
  props?: Partial<SubscriptionCancellationTrackingProps>,
): Record<string, unknown> => {
  const commonProps = buildCommonEventProps({
    screenKey: "subscription_cancellation_modal",
    pathname: context.pathname,
    search: context.search,
    businessId: context.businessId ?? null,
    userId: context.userId ?? null,
    userEmail: context.userEmail ?? null,
    planType: context.planType ?? null,
    currency: context.currency ?? null,
  });

  return {
    ...commonProps,
    reason_category: props?.reasonCategory ?? null,
    reason_text_length: props?.reasonTextLength ?? 0,
    cancel_influencer_bookings: props?.cancelInfluencerBookings ?? null,
    action: props?.action ?? null,
  };
};

export const trackUpgradeModalViewed = (
  context: BillingAnalyticsContext,
): void => {
  trackEvent(ANALYTICS_EVENT_NAMES.upgradeModalViewed, buildUpgradeModalProps(context));
};

export const trackUpgradeModalSubscribeClicked = (
  context: BillingAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.upgradeModalSubscribeClicked,
    buildUpgradeModalProps(context),
  );
};

export const trackCheckoutPageViewed = (
  context: BillingAnalyticsContext,
): void => {
  trackEvent(ANALYTICS_EVENT_NAMES.checkoutPageViewed, buildCheckoutProps(context));
};

export const trackCheckoutSuccess = (
  context: BillingAnalyticsContext,
): void => {
  trackEvent(ANALYTICS_EVENT_NAMES.checkoutSuccess, buildCheckoutProps(context));
};

export const trackSubscriptionCancellationModalViewed = (
  context: BillingAnalyticsContext,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.subscriptionCancellationModalViewed,
    buildSubscriptionCancellationProps(context),
  );
};

export const trackSubscriptionCancellationReasonSelected = (
  context: BillingAnalyticsContext,
  props: SubscriptionCancellationTrackingProps,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.subscriptionCancellationReasonSelected,
    buildSubscriptionCancellationProps(context, props),
  );
};

export const trackSubscriptionCancellationRequested = (
  context: BillingAnalyticsContext,
  props: SubscriptionCancellationTrackingProps,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.subscriptionCancellationRequested,
    buildSubscriptionCancellationProps(context, props),
  );
};

export const trackSubscriptionPauseRequested = (
  context: BillingAnalyticsContext,
  props: SubscriptionCancellationTrackingProps,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.subscriptionPauseRequested,
    buildSubscriptionCancellationProps(context, props),
  );
};

export const trackSubscriptionCancellationAbandoned = (
  context: BillingAnalyticsContext,
  props: SubscriptionCancellationTrackingProps,
): void => {
  trackEvent(
    ANALYTICS_EVENT_NAMES.subscriptionCancellationAbandoned,
    buildSubscriptionCancellationProps(context, props),
  );
};
